import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, Zap, Crown, Loader2, Calendar, Package, Sparkles, BarChart3, RefreshCw } from "lucide-react";
import { HoloCard, NeonButton, accentColor } from "@/components/Holo";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { startCheckout, estImages } from "@/lib/billing";

export default function Billing() {
  const [plans, setPlans] = useState([]);
  const [packs, setPacks] = useState([]);
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(null);
  const [packIdx, setPackIdx] = useState(1);
  const [ar, setAr] = useState({ enabled: false, threshold: 100, pack_id: null });
  const { user, refreshUser } = useAuth();

  useEffect(() => {
    api.get("/plans").then(({ data }) => setPlans(data)).catch(() => {});
    api.get("/packs").then(({ data }) => { setPacks(data); }).catch(() => {});
    api.get("/credits/usage").then(({ data }) => setUsage(data)).catch(() => {});
  }, []);
  useEffect(() => { if (user?.auto_recharge) setAr({ ...user.auto_recharge, pack_id: user.auto_recharge.pack_id || packs[1]?.id }); }, [user, packs]);

  const selectedPack = packs[packIdx] || packs[0];

  const subscribe = async (plan) => {
    if (plan.price_label || plan.price <= 0) { window.location.href = "mailto:sales@futra.ai?subject=FUTRA%20Enterprise%20Inquiry"; return; }
    setLoading(plan.name);
    try { await startCheckout({ kind: "subscription", plan_name: plan.name }); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); setLoading(null); }
  };
  const buyPack = async () => {
    if (!selectedPack) return;
    setLoading("pack");
    try { await startCheckout({ kind: "credits", pack_id: selectedPack.id }); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); setLoading(null); }
  };
  const saveAuto = async (enabled) => {
    try {
      const { data } = await api.put("/payments/auto-recharge", { enabled, threshold: Number(ar.threshold) || 100, pack_id: ar.pack_id || selectedPack?.id, monthly_cap: ar.monthly_cap ?? 50 });
      setAr(data); await refreshUser();
      toast.success(enabled ? "Auto Recharge enabled" : "Auto Recharge disabled");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const renewal = user?.renewal_date ? new Date(user.renewal_date).toLocaleDateString() : "—";

  return (
    <div data-testid="module-billing" className="space-y-12">
      <div>
        <h1 className="font-display font-bold text-2xl sm:text-3xl flex items-center gap-3"><Crown className="text-neon-yellow" /> Billing & Credits</h1>
        <p className="font-mono-tech text-white/40 text-sm mt-1">Manage your operating license, credits and recharge preferences.</p>
      </div>

      {/* CURRENT PLAN */}
      <section data-testid="current-plan">
        <p className="font-mono-tech text-neon-turquoise text-[11px] uppercase tracking-[0.3em] mb-3">// Current Plan</p>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <HoloCard accent="turquoise" className="p-5" hover={false}>
            <div className="font-mono-tech text-[10px] uppercase tracking-widest text-white/40">Plan</div>
            <div className="font-display font-black text-2xl mt-1 text-neon-turquoise">{user?.plan}</div>
          </HoloCard>
          <HoloCard accent="pink" className="p-5" hover={false} testid="monthly-credits-card">
            <div className="font-mono-tech text-[10px] uppercase tracking-widest text-white/40">Monthly Credits</div>
            <div className="font-display font-black text-2xl mt-1 flex items-center gap-1"><Zap size={16} className="text-neon-pink" /> {user?.monthly_credits?.toLocaleString() ?? 0}</div>
          </HoloCard>
          <HoloCard accent="green" className="p-5" hover={false} testid="purchased-credits-card">
            <div className="font-mono-tech text-[10px] uppercase tracking-widest text-white/40">Purchased Credits</div>
            <div className="font-display font-black text-2xl mt-1 flex items-center gap-1"><Package size={16} className="text-neon-green" /> {user?.purchased_credits?.toLocaleString() ?? 0}</div>
            {user?.legacy_credits > 0 && <div className="font-mono-tech text-[9px] text-white/30 mt-1">+{user.legacy_credits.toLocaleString()} legacy</div>}
          </HoloCard>
          <HoloCard accent="yellow" className="p-5" hover={false}>
            <div className="font-mono-tech text-[10px] uppercase tracking-widest text-white/40">Renewal</div>
            <div className="font-display font-black text-lg mt-1 flex items-center gap-1"><Calendar size={15} className="text-neon-yellow" /> {renewal}</div>
          </HoloCard>
        </div>
      </section>

      {/* UPGRADE PLANS */}
      <section data-testid="upgrade-plans">
        <p className="font-mono-tech text-neon-pink text-[11px] uppercase tracking-[0.3em] mb-3">// Upgrade Plans</p>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
          {plans.map((p) => {
            const c = accentColor(p.accent);
            const current = user?.plan === p.name;
            const isEnterprise = p.price_label || p.price <= 0;
            return (
              <HoloCard key={p.id} accent={p.accent} className={`p-6 flex flex-col ${p.highlight ? "lg:scale-[1.04]" : ""}`} testid={`billing-plan-${p.name}`}>
                {p.highlight && <span className="absolute top-4 right-4 font-mono-tech text-[9px] uppercase tracking-widest px-2 py-0.5" style={{ color: c.hex, border: `1px solid ${c.hex}` }}>Recommended</span>}
                <h3 className="font-display font-bold text-xl" style={{ color: c.hex }}>{p.name}</h3>
                <div className="mt-4 flex items-end gap-1">
                  {isEnterprise
                    ? <span className="font-display font-black text-4xl" style={{ color: c.hex }}>{p.price_label || "Custom"}</span>
                    : <><span className="font-display font-black text-4xl">${p.price}</span><span className="text-white/40 font-mono-tech text-xs mb-1">/mo</span></>}
                </div>
                <p className="font-mono-tech text-[11px] text-white/50 mt-2">{isEnterprise ? "Tailored to your studio" : p.credits.toLocaleString() + " credits / month"}</p>
                <div className="h-px bg-white/10 my-5" />
                <ul className="space-y-2.5 flex-1">
                  {p.features.map((ft) => (<li key={ft} className="flex gap-2 text-[13px] text-white/70"><Check size={14} style={{ color: c.hex }} className="shrink-0 mt-0.5" /> {ft}</li>))}
                </ul>
                <NeonButton accent={p.accent} className="mt-6 w-full" disabled={current || loading === p.name} onClick={() => subscribe(p)} data-testid={`subscribe-${p.name}`}>
                  {loading === p.name ? <><Loader2 size={14} className="animate-spin" /> Redirecting…</> : current ? "Current Plan" : isEnterprise ? "Contact Sales" : `Subscribe`}
                </NeonButton>
              </HoloCard>
            );
          })}
        </div>
      </section>

      {/* EXTRA CREDITS SLIDER */}
      <section data-testid="extra-credits">
        <p className="font-mono-tech text-neon-green text-[11px] uppercase tracking-[0.3em] mb-3">// Extra Credits · One-Time</p>
        <HoloCard accent="green" className="p-6" hover={false}>
          <div className="grid lg:grid-cols-[1fr_260px] gap-8 items-center">
            <div>
              <div className="flex items-baseline justify-between mb-2">
                <span className="font-mono-tech text-[10px] uppercase tracking-widest text-white/50">Select amount</span>
                <span className="font-mono-tech text-[11px] text-neon-green">{selectedPack?.multiplier}</span>
              </div>
              <input type="range" min={0} max={Math.max(0, packs.length - 1)} step={1} value={packIdx}
                onChange={(e) => setPackIdx(Number(e.target.value))} data-testid="credit-slider"
                className="w-full accent-[#39ff14] cursor-pointer" />
              <div className="flex justify-between mt-2">
                {packs.map((p, i) => (
                  <button key={p.id} onClick={() => setPackIdx(i)} data-testid={`slider-stop-${i}`}
                    className="font-mono-tech text-[10px] transition-colors" style={{ color: i === packIdx ? "#39ff14" : "rgba(255,255,255,0.35)" }}>
                    ${p.price}
                  </button>
                ))}
              </div>
              <div className="grid grid-cols-3 gap-3 mt-6">
                <div className="border border-white/10 p-3"><div className="font-mono-tech text-[9px] uppercase text-white/40">Credits</div><div className="font-display font-bold text-xl text-neon-green">+{selectedPack?.credits?.toLocaleString()}</div></div>
                <div className="border border-white/10 p-3"><div className="font-mono-tech text-[9px] uppercase text-white/40">≈ Images</div><div className="font-display font-bold text-xl">{estImages(selectedPack?.credits)}</div></div>
                <div className="border border-white/10 p-3"><div className="font-mono-tech text-[9px] uppercase text-white/40">Price</div><div className="font-display font-bold text-xl">${selectedPack?.price}</div></div>
              </div>
            </div>
            <div className="text-center border border-neon-green/30 p-6">
              <Sparkles size={26} className="text-neon-green mx-auto mb-3" />
              <div className="font-display font-black text-3xl text-neon-green">+{selectedPack?.credits?.toLocaleString()}</div>
              <div className="font-mono-tech text-[10px] uppercase tracking-widest text-white/40 mt-1">purchased credits</div>
              <NeonButton accent="green" className="w-full mt-5" onClick={buyPack} disabled={loading === "pack"} data-testid="buy-pack-btn">
                {loading === "pack" ? <><Loader2 size={14} className="animate-spin" /> Redirecting…</> : `Buy for $${selectedPack?.price}`}
              </NeonButton>
              <p className="font-mono-tech text-[9px] text-white/25 mt-3">Rolls over · never expires</p>
            </div>
          </div>
        </HoloCard>
      </section>

      {/* AUTO RECHARGE */}
      <section data-testid="auto-recharge-section">
        <p className="font-mono-tech text-neon-turquoise text-[11px] uppercase tracking-[0.3em] mb-3">// Auto Recharge · Assisted</p>
        <HoloCard accent="turquoise" className="p-6" hover={false}>
          <div className="flex flex-col lg:flex-row lg:items-end gap-4 justify-between">
            <div className="flex items-center gap-3 font-mono-tech text-sm text-white/70 flex-wrap">
              <RefreshCw size={16} className="text-neon-turquoise" /> When credits drop below
              <select value={ar.threshold} onChange={(e) => setAr((p) => ({ ...p, threshold: e.target.value }))} data-testid="billing-ar-threshold"
                className="bg-black/40 border border-white/15 px-2 py-1 text-white text-xs">
                {[50, 100, 250, 500].map((t) => <option key={t} value={t} className="bg-[#0d0d0f]">{t}</option>)}
              </select>
              buy
              <select value={ar.pack_id || ""} onChange={(e) => setAr((p) => ({ ...p, pack_id: e.target.value }))} data-testid="billing-ar-pack"
                className="bg-black/40 border border-white/15 px-2 py-1 text-white text-xs">
                {packs.map((p) => <option key={p.id} value={p.id} className="bg-[#0d0d0f]">+{p.credits} (${p.price})</option>)}
              </select>
              · cap
              <select value={ar.monthly_cap ?? 50} onChange={(e) => setAr((p) => ({ ...p, monthly_cap: Number(e.target.value) }))} data-testid="billing-ar-cap"
                className="bg-black/40 border border-white/15 px-2 py-1 text-white text-xs">
                <option value={25} className="bg-[#0d0d0f]">$25/mo</option>
                <option value={50} className="bg-[#0d0d0f]">$50/mo</option>
                <option value={100} className="bg-[#0d0d0f]">$100/mo</option>
                <option value={0} className="bg-[#0d0d0f]">Unlimited</option>
              </select>
            </div>
            <NeonButton accent={ar.enabled ? "pink" : "turquoise"} onClick={() => saveAuto(!ar.enabled)} data-testid="billing-ar-toggle">
              {ar.enabled ? "Enabled — Disable" : "Enable Auto Recharge"}
            </NeonButton>
          </div>
          <p className="font-mono-tech text-[10px] text-white/30 mt-3">
            {ar.enabled ? `Active · $${(ar.spent_this_month || 0).toFixed(2)} of ${ar.monthly_cap ? "$" + Number(ar.monthly_cap).toFixed(0) : "unlimited"} used this cycle. ` : ""}
            No silent charges — you'll always confirm each recharge with one click.
          </p>
        </HoloCard>
      </section>

      {/* ANALYTICS */}
      <section data-testid="billing-analytics">
        <p className="font-mono-tech text-neon-purple text-[11px] uppercase tracking-[0.3em] mb-3">// Credit Analytics</p>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <HoloCard accent="purple" className="p-5" hover={false}><BarChart3 size={16} className="text-neon-purple mb-2" /><div className="font-display font-black text-2xl">{usage?.credits_used?.toLocaleString() ?? 0}</div><div className="font-mono-tech text-[10px] uppercase text-white/40 mt-1">Credits Used</div></HoloCard>
          <HoloCard accent="green" className="p-5" hover={false}><RefreshCw size={16} className="text-neon-green mb-2" /><div className="font-display font-black text-2xl">{usage?.credits_refunded?.toLocaleString() ?? 0}</div><div className="font-mono-tech text-[10px] uppercase text-white/40 mt-1">Credits Refunded</div></HoloCard>
          <HoloCard accent="turquoise" className="p-5" hover={false}><Zap size={16} className="text-neon-turquoise mb-2" /><div className="font-display font-black text-2xl">{user?.credits?.toLocaleString() ?? 0}</div><div className="font-mono-tech text-[10px] uppercase text-white/40 mt-1">Total Remaining</div></HoloCard>
          <HoloCard accent="yellow" className="p-5" hover={false}><Sparkles size={16} className="text-neon-yellow mb-2" /><div className="font-display font-black text-2xl">{estImages(user?.credits)}</div><div className="font-mono-tech text-[10px] uppercase text-white/40 mt-1">≈ Images Left</div></HoloCard>
        </div>
      </section>

      <p className="font-mono-tech text-[10px] text-white/30">Secure checkout via Stripe. Test mode — use card 4242 4242 4242 4242, any future date & CVC.</p>
    </div>
  );
}
